import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { format } from "date-fns";
import { ArrowLeft } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";
import { api } from "../lib/api";

const CAT_COLOR = { friendly: "#CD7F32", challenge: "#C0C0C0", cup: "#FFD700", prerelease: "#7000FF" };

export default function PlayerPage() {
  const { playerId } = useParams();
  const [p, setP] = useState(null);
  const [error, setError] = useState("");
  usePageMeta(p ? `${p.name} — Player Ranking` : "Player Profile");

  useEffect(() => {
    api
      .get(`/players/${playerId}`)
      .then(({ data }) => setP(data))
      .catch(() => setError("Player not found."));
  }, [playerId]);

  if (error) return <p className="mx-auto max-w-5xl px-5 py-16 text-neutral-400">{error}</p>;
  if (!p) return <p className="mx-auto max-w-5xl px-5 py-16 text-neutral-500">Loading…</p>;

  return (
    <div className="mx-auto max-w-5xl px-5 lg:px-10 py-10 lg:py-16" data-testid="player-profile">
      <Link to="/leaderboard" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-500 hover:text-primary transition-colors duration-200" data-testid="back-to-leaderboard">
        <ArrowLeft className="h-4 w-4" /> Leaderboards
      </Link>

      <div className="mt-4 grid gap-3 md:grid-cols-3">
        <div className="md:col-span-2 p-6 border border-white/10 rounded-md bg-white/[0.02]">
          <p className="font-mono text-xs text-neutral-500">POP ID {p.player_id}</p>
          <h1 className="mt-1 font-display font-black text-3xl sm:text-4xl tracking-tighter" data-testid="player-name">{p.name}</h1>
          <div className="mt-5 grid grid-cols-3 gap-4">
            <div>
              <div className="font-mono text-3xl text-primary" data-testid="player-points">{p.totals.points}</div>
              <div className="text-[11px] uppercase tracking-wider text-neutral-500">Points</div>
            </div>
            <div>
              <div className="font-mono text-3xl">{p.win_rate}%</div>
              <div className="text-[11px] uppercase tracking-wider text-neutral-500">Win rate</div>
            </div>
            <div>
              <div className="font-mono text-3xl">{p.matches_played}</div>
              <div className="text-[11px] uppercase tracking-wider text-neutral-500">Matches</div>
            </div>
          </div>
        </div>

        <div className="p-6 border border-white/10 rounded-md bg-primary/5">
          <div className="text-[11px] uppercase tracking-wider text-neutral-500">Overall rank</div>
          <div className="font-mono text-5xl text-primary mt-2" data-testid="player-rank">#{p.rank ?? "—"}</div>
          {p.division && p.division !== "unknown" && (
            <div className="mt-2 font-mono text-xs text-neutral-400" data-testid="player-division">
              {p.division.toUpperCase()} · #{p.division_rank ?? "—"} in division
            </div>
          )}
          <div className="mt-4 font-mono text-sm">
            <span className="text-win">{p.totals.wins}W</span> · <span className="text-draw">{p.totals.draws}D</span> ·{" "}
            <span className="text-loss">{p.totals.losses}L</span>
          </div>
        </div>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        {["friendly", "challenge", "cup"].map((c) => {
          const s = p.by_category[c];
          return (
            <div key={c} className="p-4 border border-white/10 rounded-md" data-testid={`player-cat-${c}`}>
              <div className="text-[11px] uppercase tracking-[0.2em]" style={{ color: CAT_COLOR[c] }}>{c}</div>
              <div className="font-mono text-2xl mt-1">{s ? s.points : 0}<span className="text-xs text-neutral-500"> pts</span></div>
              <div className="font-mono text-[11px] text-neutral-500 mt-1">
                {s ? `${s.events} events · ${s.wins}W/${s.draws}D/${s.losses}L` : "No events yet"}
              </div>
            </div>
          );
        })}
      </div>

      <h2 className="mt-10 text-base md:text-lg font-display">Tournament history</h2>
      <div className="mt-3 space-y-3" data-testid="player-history">
        {p.history.map((h) => (
          <div key={h.tournament_id} className="border border-white/10 rounded-md p-4">
            <div className="flex flex-wrap items-center gap-3 justify-between">
              <div>
                <Link to={`/results/${h.tournament_id}`} className="font-display text-base hover:text-primary transition-colors duration-200">
                  {h.tournament}
                </Link>
                <div className="font-mono text-[11px] text-neutral-500 mt-0.5">
                  {format(new Date(h.date), "d MMM yyyy")} · <span style={{ color: CAT_COLOR[h.category] }}>{h.category}</span>
                  {!h.ranked && " · unranked"}
                </div>
              </div>
              <div className="font-mono text-sm">
                <span className="text-primary font-bold">{h.points} pts</span>{" "}
                <span className="text-neutral-500">({h.wins}-{h.draws}-{h.losses})</span>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {h.matches.map((m, i) => (
                <span
                  key={i}
                  className="text-[11px] font-mono px-2 py-1 rounded-sm border"
                  style={{
                    borderColor:
                      m.outcome === "win" || m.outcome === "bye" ? "#00FF6655" : m.outcome === "draw" ? "#FFEA0055" : "#FF3B3055",
                    color: m.outcome === "win" || m.outcome === "bye" ? "#00FF66" : m.outcome === "draw" ? "#FFEA00" : "#FF3B30",
                  }}
                >
                  R{m.round} {m.outcome.toUpperCase()} vs {m.opponent}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
